import React, { useEffect, useRef, useState } from 'react';

type CountUpProps = {
  to: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  /** Count duration in ms. */
  duration?: number;
  /** Pause after the figure scrolls into view, so it starts once it has faded in. */
  delay?: number;
};

const format = (n: number, decimals: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// A figure that counts up from zero the first time it scrolls into view,
// easing out as it lands. The real text is always the final figure, set in
// transparent ink: it sizes the box (no width change mid-count) and is what
// screen readers, copy-paste and crawlers get. The counting digits are a
// decorative overlay on top. With reduced motion it simply shows the final
// figure.
export default function CountUp({
  to,
  decimals = 0,
  prefix = '',
  suffix = '',
  duration = 1400,
  delay = 200,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      setValue(to);
      return;
    }
    let raf = 0;
    let timer = 0;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        timer = window.setTimeout(() => {
          const start = performance.now();
          const tick = (now: number) => {
            const t = Math.min(1, (now - start) / duration);
            setValue(to * (1 - Math.pow(1 - t, 4)));
            if (t < 1) raf = requestAnimationFrame(tick);
          };
          raf = requestAnimationFrame(tick);
        }, delay);
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [to, duration, delay]);

  const final = `${prefix}${format(to, decimals)}${suffix}`;
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <span style={{ color: 'transparent' }}>{final}</span>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          whiteSpace: 'nowrap',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      >
        {prefix}
        {format(value, decimals)}
        {suffix}
      </span>
    </span>
  );
}
